import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertServiceService } from 'src/app/service/alert/alert-service.service';
import { MerchantFormService } from 'src/app/service/APIs/merchant-form.service';

@Component({
  selector: 'app-pos-merchant-modal',
  templateUrl: './pos-merchant-modal.component.html',
  styleUrls: ['./pos-merchant-modal.component.scss']
})
export class PosMerchantModalComponent implements OnInit {

  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  currentStep = 1;
  public step1Form: FormGroup | any | undefined;
  public step2Form: FormGroup | any | undefined;
  public step3Form: FormGroup | any | undefined;

  idDocumentName: string = '';
  proofOfAddressName: string = '';
  idDocumentFile: File | null = null;
  proofOfAddressFile: File | null = null;
  submissionAlert: { type: 'success' | 'error'; message: string; errors: string[] } | null = null;
  isSubmitting = false;

  nigerianStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue',
    'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu',
    'FCT Abuja', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina',
    'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo',
    'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
  ];

  constructor(
    private fb: FormBuilder,
    private alert: AlertServiceService,
    private merchantFormService: MerchantFormService
  ) { }

  closeModal() {
    this.currentStep = 1;
    this.step1Form.reset();
    this.step2Form.reset();
    this.step3Form.reset();
    this.idDocumentName = '';
    this.proofOfAddressName = '';
    this.idDocumentFile = null;
    this.proofOfAddressFile = null;
    this.submissionAlert = null;
    this.isSubmitting = false;
    document.body.style.overflow = '';
    this.closed.emit();
  }

  nextStep() {
    if (this.currentStep === 1) {
      if (this.step1Form.valid) {
        this.currentStep = 2;
      } else {
        this.step1Form.markAllAsTouched();
      }
    } else if (this.currentStep === 2) {
      if (this.step2Form.valid) {
        this.currentStep = 3;
      } else {
        this.step2Form.markAllAsTouched();
      }
    }
  }

  completeRegistration() {
    if (this.step3Form.invalid) {
      this.step3Form.markAllAsTouched();
      return;
    }

    const s1 = this.step1Form.value;
    const s2 = this.step2Form.value;
    const s3 = this.step3Form.value;

    const formData = new FormData();

    formData.append('title', s1.title);
    formData.append('fullName', s1.fullName);
    formData.append('maritalStatus', s1.maritalStatus);
    formData.append('dateOfBirth', s1.dateOfBirth);
    formData.append('stateOfOrigin', s1.stateOfOrigin);
    formData.append('lga', s1.lga);
    formData.append('address', s1.address);
    formData.append('emailAddress', s1.email);
    formData.append('phoneNumber', s1.phone);
    formData.append('employmentStatus', s1.employmentStatus);

    if (s2.bvn) { formData.append('bvn', s2.bvn); }
    formData.append('meansOfIdentification', s2.meansOfId);
    if (this.idDocumentFile) {
      formData.append('identificationDocument', this.idDocumentFile, this.idDocumentFile.name);
    }
    if (this.proofOfAddressFile) {
      formData.append('proofOfAddressFile', this.proofOfAddressFile, this.proofOfAddressFile.name);
    }
    formData.append('proofOfAddress', s2.meansOfVerification);

    formData.append('nextOfKinTitle', s3.kinTitle);
    formData.append('nextOfKinFullName', s3.kinFullName);
    formData.append('nextOfKinDateOfBirth', s3.kinDateOfBirth);
    formData.append('relationshipWithNextOfKin', s3.kinRelationship);
    formData.append('nextOfKinStateOfOrigin', s3.kinStateOfOrigin);
    formData.append('nextOfKinAddress', s3.kinAddress);
    formData.append('nextOfKinPhoneNumber', s3.kinPhone);
    formData.append('nextOfKinEmail', s3.kinEmail ?? '');

    this.submissionAlert = null;
    this.isSubmitting = true;
    this.merchantFormService.submitFormData(formData).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        this.submissionAlert = {
          type: 'success',
          message: res?.message ?? 'Registration submitted successfully!',
          errors: []
        };
        setTimeout(() => this.closeModal(), 2000);
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.submissionAlert = {
          type: 'error',
          message: err?.error?.message ?? 'Submission failed. Please try again.',
          errors: err?.error?.errors ?? []
        };
      }
    });
  }

  onIdDocumentChange(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.idDocumentFile = file;
      this.idDocumentName = file.name;
      this.step2Form.get('idDocument').setValue(file.name);
    }
  }

  onProofOfAddressChange(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.proofOfAddressFile = file;
      this.proofOfAddressName = file.name;
      this.step2Form.get('proofOfAddress').setValue(file.name);
    }
  }

  ngOnInit(): void {
    this.step1Form = this.fb.group({
      title: ['', Validators.required],
      fullName: ['', Validators.required],
      maritalStatus: ['', Validators.required],
      dateOfBirth: ['', Validators.required],
      stateOfOrigin: ['', Validators.required],
      lga: ['', Validators.required],
      address: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      employmentStatus: ['', Validators.required],
    });

    this.step2Form = this.fb.group({
      bvn: ['', [Validators.minLength(11), Validators.maxLength(11)]],
      meansOfId: ['', Validators.required],
      idDocument: ['', Validators.required],
      meansOfVerification: ['', Validators.required],
      proofOfAddress: ['', Validators.required],
    });

    this.step3Form = this.fb.group({
      kinTitle: ['', Validators.required],
      kinFullName: ['', Validators.required],
      kinDateOfBirth: ['', Validators.required],
      kinRelationship: ['', Validators.required],
      kinStateOfOrigin: ['', Validators.required],
      kinLga: ['', Validators.required],
      kinAddress: ['', Validators.required],
      kinPhone: ['', Validators.required],
      kinEmail: [''],
    });
  }
}
